import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Accordion, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faMapLocationDot,
  faCheckCircle,
  faBoxOpen,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
  faCalendarDays,
  faClock,
} from '@fortawesome/free-regular-svg-icons';

// @ts-ignore
import styles from './Faq.module.scss?module';
import { useUser } from '../../../../../Context/UserContext';
import { useVts } from '../../../../../Context/VtsContext';
import {
  getVtsPhase,
  getPhaseDeadline,
  formatTimeUntil,
  VtsPhase,
} from '../../../../../Helper/vts';
import { VtsInterface } from '../../../../../../Interface/Vts';

const MESSAGES_PATH = '/virtual-trade-show/help-center/messages';
const EXHIBITION_PATH = '/virtual-trade-show/exhibition';

const formatLong = (iso?: string, withTime = false) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        weekday: 'long',
        month: 'long',
        day: '2-digit',
        year: 'numeric',
        ...(withTime
          ? { hour: '2-digit', minute: '2-digit', hour12: false }
          : {}),
      })
    : '';

const formatShort = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
      })
    : '';

type StepKey = 'order' | 'confirmation' | 'release';
type StepStatus = 'done' | 'current' | 'upcoming';

const STEP_TITLE: Record<StepKey, string> = {
  order: 'Order',
  confirmation: 'Confirmation',
  release: 'Release',
};

const STEP_DESCRIPTION: Record<StepKey, string> = {
  order: 'Place your order — quantities are unlimited.',
  confirmation: 'Review and confirm your final VTS order.',
  release: 'Retrieve your order — fully or in partial releases.',
};

const FINAL_RELEASE_LABEL = 'until June 30';

const formatStepRange = (startIso?: string, endIso?: string): string => {
  if (!startIso || !endIso) return '';
  const start = new Date(startIso);
  const end = new Date(endIso);
  const startMonth = start.toLocaleString('en-US', { month: 'short' });
  const endMonth = end.toLocaleString('en-US', { month: 'short' });
  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${start.getDate()} – ${end.getDate()}`;
  }
  return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}`;
};

const getStepStatus = (phase: VtsPhase, step: StepKey): StepStatus => {
  if (phase === 'before' || phase === 'order') {
    return step === 'order' ? 'current' : 'upcoming';
  }
  if (phase === 'ordered' || phase === 'confirmation') {
    if (step === 'order') return 'done';
    if (step === 'confirmation') return 'current';
    return 'upcoming';
  }
  // confirmed
  if (step === 'release') return 'current';
  return 'done';
};

const getStepRange = (vts: VtsInterface, step: StepKey): string => {
  if (step === 'order') return formatStepRange(vts.order_start, vts.order_end);
  if (step === 'confirmation')
    return formatStepRange(vts.confirmation_start, vts.confirmation_end);
  return FINAL_RELEASE_LABEL;
};

const getStepStart = (vts: VtsInterface, step: StepKey): Date => {
  if (step === 'order') return new Date(vts.order_start);
  if (step === 'confirmation') return new Date(vts.confirmation_start);
  return new Date(vts.confirmation_end);
};

const getStepFooter = (
  vts: VtsInterface,
  step: StepKey,
  status: StepStatus,
  phaseDeadline: Date | null
): string | null => {
  if (status === 'done') return null;
  if (step === 'release') return `Available ${FINAL_RELEASE_LABEL}`;
  if (status === 'current' && phaseDeadline) {
    return `Ends ${formatTimeUntil(phaseDeadline)}`;
  }
  return `Starts ${formatTimeUntil(getStepStart(vts, step))}`;
};

const ORDER_BY_PHASE: Record<VtsPhase, string[]> = {
  before: [
    'opening-hours',
    'navigation',
    'top-menu',
    'exhibition',
    'login',
    'naskor-booth',
    'confirmation-when',
    'confirmation-what',
    'release-order',
  ],
  order: [
    'exhibition',
    'top-menu',
    'navigation',
    'opening-hours',
    'login',
    'naskor-booth',
    'confirmation-when',
    'confirmation-what',
    'release-order',
  ],
  ordered: [
    'confirmation-when',
    'confirmation-what',
    'release-order',
    'exhibition',
    'top-menu',
    'navigation',
    'opening-hours',
    'login',
    'naskor-booth',
  ],
  confirmation: [
    'confirmation-what',
    'confirmation-when',
    'release-order',
    'exhibition',
    'top-menu',
    'navigation',
    'login',
    'naskor-booth',
    'opening-hours',
  ],
  confirmed: [
    'release-order',
    'confirmation-what',
    'confirmation-when',
    'exhibition',
    'top-menu',
    'login',
    'naskor-booth',
    'navigation',
    'opening-hours',
  ],
};

type ActionCard = {
  icon: typeof faComments;
  label: string;
  description: string;
  onClick: () => void;
  hideOnMobile?: boolean;
};

export default function Faq() {
  const userContext = useUser();
  const { vts } = useVts();
  const location = useLocation();
  const navigate = useNavigate();
  const finalOrderReleaseDate = '30th of June 2026';

  const [activeKey, setActiveKey] = useState<string | null>(null);

  useEffect(() => {
    if (!location.hash) return;
    const key = location.hash.replace(/^#/, '');
    if (!key) return;
    setActiveKey(key);
    requestAnimationFrame(() => {
      const el = document.getElementById(`faq-${key}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash]);

  const phase: VtsPhase | null = vts ? getVtsPhase(vts) : null;
  const phaseDeadline = vts && phase ? getPhaseDeadline(vts, phase) : null;
  const isExhibitor = !!userContext.user?.is_exhibitor;
  const order = phase ? ORDER_BY_PHASE[phase] : ORDER_BY_PHASE.order;

  const items: Record<string, { title: React.ReactNode; body: React.ReactNode } | null> = {
    'opening-hours': isExhibitor
      ? null
      : {
          title: 'Opening hours',
          body: (
            <>
              Start: {formatLong(vts?.order_start, true)}
              <br />
              End: {formatLong(vts?.order_end, true)}
            </>
          ),
        },
    login: {
      title: 'Login',
      body: (
        <>
          Please make sure to be always logged in with your Naskorsports user
          account to have full access to all features.
        </>
      ),
    },
    navigation: {
      title: 'How can I navigate through the Virtual Trade Show?',
      body: (
        <>
          Use the bottom toolbar to visit the different locations:
          <ul>
            <li>Lobby</li>
            <li>Exhibition</li>
            <li>Help Center</li>
          </ul>
          Click on the button to go to the location you want to visit. In the
          Exhibition Hall, click on the highlighted circles on the ground to
          navigate. If you want to visit a brand, click on the booth. Click on
          the highlighted circles in front of the booth counter to use the
          features of the brand booth.
        </>
      ),
    },
    'top-menu': {
      title: 'What do the top menu buttons show?',
      body: (
        <ul>
          <li>
            <strong>Exhibitors:</strong> See all brands – click on a brand to go
            directly to its booth.
          </li>
          {!isExhibitor && (
            <li>
              <strong>Order overview:</strong> See all ordered products and
              samples at a glance.
            </li>
          )}
          <li>
            <strong>Account:</strong> Login with your Naskorsports credentials /
            Review your interaction points (attractive prizes are waiting for
            you)
          </li>
        </ul>
      ),
    },
    exhibition: {
      title: 'What can I do in the Exhibition Area?',
      body: (
        <>
          You can visit all brands at their booths and use the following
          features:
          <ul>
            <li>
              <strong>Showcase Area:</strong> Here you'll find a selection of
              top products that absolutely belong in your portfolio. Click on
              the display cases to explore all the details.
            </li>
            <li>
              <strong>Highlights:</strong> Check out current brand highlights
              and be up to date about key products.
            </li>
            <li>
              <strong>New products:</strong> Get informed about the latest
              product launches of your favourite brands. Don´t miss out the
              newest stuff!
            </li>
            {!isExhibitor && (
              <li>
                <strong>Place order:</strong> Click on the booth counter and
                select "place order". Now you can enter your desired quantities
                into the product list. Don´t forget to add the desired
                quantities with the "ADD TO ORDER" button or "ADD ALL TO ORDER"
                BUTTON.
                <br />
                You can place and adjust your order between{' '}
                {formatShort(vts?.order_start)} and{' '}
                {formatShort(vts?.order_end)} until 23:59. For an overview of
                your orders, click "Order overview" in the top right menu.
                <br />
                On {formatLong(vts?.order_end, true)}, all placed orders from
                your "Order overview" will be processed to your overall Virtual
                Trade Show order in your Naskorsports account details on
                https://naskorsports.com. Please adjust and confirm your order
                until {formatLong(vts?.confirmation_end, true)}.
              </li>
            )}
            {!isExhibitor && (
              <li>
                <strong>Free samples:</strong> Click on the booth counter and
                select "free samples". Choose your samples and confirm with the
                "Add to order" button at the bottom of the list. See all
                collected samples in your order overview.
                <br />
                <strong>Please note:</strong> Sample stock is limited – first
                come, first serve. Please check your order overview in the top
                right menu and the sample feature at the brand booth for
                available quantities. Please note that you cannot change your
                samples after the end of the Virtual Trade Show. The delivery
                of the selected samples depends on availability and cannot be
                guaranteed. Samples will be delivered with your Virtual Trade
                Show shipment(s).
              </li>
            )}
            <li>
              <strong>Give feedback:</strong> Click on the booth counter and
              select "give feedback". Answer the questions giving 1 to 4 star
              ratings (1 = very bad, 4 = very good). Answer all questions to
              submit your feedback.
            </li>
            <li>
              <strong>Media center:</strong> Click on the booth counter and
              select "media center". Watch and download content from different
              categories. Download a "Complete media kit" for your own marketing
              activities.
            </li>
          </ul>
        </>
      ),
    },
    'naskor-booth': {
      title: 'Naskorsports Booth',
      body: (
        <>
          Visit us at our Naskorsports Booth in the Hall B2B! Click on the
          counter to watch our latest image video and give us your valuable
          feedback.
        </>
      ),
    },
    'confirmation-when': isExhibitor
      ? null
      : {
          title: 'When can I confirm my order?',
          body: (
            <>
              The order confirmation period is from{' '}
              {formatLong(vts?.confirmation_start, true)} until{' '}
              {formatLong(vts?.confirmation_end, true)}.
            </>
          ),
        },
    'confirmation-what': isExhibitor
      ? null
      : {
          title: 'What do I need to do during the confirmation period?',
          body: (
            <>
              The Virtual Trade Show is closed. Now you need to confirm your
              order, which you placed during the event, in your Naskorsports
              account details on https://naskorsports.com. For brands, where a
              minimum of one item was ordered during the Virtual Trade Show,
              you can add or adjust items from the entire product portfolio of
              this brand to your final Virtual Trade Show order until{' '}
              {formatLong(vts?.confirmation_end, true)}.
            </>
          ),
        },
    'release-order':
      userContext.user && !userContext.user.is_exhibitor
        ? {
            title: 'How can I release my Virtual Trade Show order?',
            body: (
              <>
                After you confirmed your order, you can retrieve the entire
                order or parts of the order in your Naskorsports account details
                on https://naskorsports.com.
                <br />
                Until {finalOrderReleaseDate} you can retrieve the ordered
                products in several partial releases. These shipments will be
                invoiced accordingly.
                <br />
                Products that are not in stock when you want to retrieve them,
                can be retrieved directly after we receive them. So, stay tuned
                for the expected time of arrival in the service center.
                <br />
                After you released an order, you will receive your order
                confirmation as usual.
              </>
            ),
          }
        : null,
  };

  const actions: ActionCard[] = (() => {
    const exhibition: ActionCard = {
      icon: faMapLocationDot,
      label: 'Visit the Exhibition',
      description: 'Walk through the halls and discover all brand booths.',
      onClick: () => navigate(EXHIBITION_PATH),
      hideOnMobile: true,
    };
    const chat: ActionCard = {
      icon: faComments,
      label: 'Start a chat',
      description: "Can't find your answer? Message us directly.",
      onClick: () => navigate(MESSAGES_PATH),
      hideOnMobile: true,
    };
    const confirm: ActionCard = {
      icon: faCheckCircle,
      label: 'Confirm your order',
      description: 'Review and confirm your VTS order before the deadline.',
      onClick: () => navigate('/vts-order'),
    };
    const release: ActionCard = {
      icon: faBoxOpen,
      label: 'Release your order',
      description: 'Retrieve your order — fully or in partial releases.',
      onClick: () => navigate('/vts-order'),
    };

    if (isExhibitor) return [exhibition, chat];
    if (phase === 'confirmation') return [confirm, exhibition, chat];
    if (phase === 'confirmed') return [release, exhibition, chat];
    return [exhibition, chat];
  })();

  const steps: StepKey[] = ['order', 'confirmation', 'release'];

  return (
    <>
      {vts && phase && !isExhibitor && (
        <Row className="pb-4">
          <Col>
            <div className={styles.PhaseRoadmapBlock}>
              <div className={styles.PhaseRoadmapHeader}>
                Where you are in the process
              </div>
              <div className={styles.PhaseRoadmapSubtitle}>
                Track your progress through the Virtual Trade Show.
              </div>
              <div className={styles.PhaseRoadmap}>
              {steps.map((step, idx) => {
                const status = getStepStatus(phase, step);
                const range = getStepRange(vts, step);
                const isCurrent = status === 'current';
                const stepFooter = getStepFooter(vts, step, status, phaseDeadline);
                return (
                  <React.Fragment key={step}>
                    <div
                      className={[
                        styles.PhaseStep,
                        styles[`PhaseStep_${status}`],
                      ].join(' ')}
                    >
                      <div className={styles.PhaseStepHeader}>
                        <span className={styles.PhaseStepMarker}>
                          {status === 'done' ? (
                            <FontAwesomeIcon icon={faCheck} />
                          ) : (
                            idx + 1
                          )}
                        </span>
                        <span className={styles.PhaseStepTitle}>
                          {STEP_TITLE[step]}
                        </span>
                        {isCurrent && (
                          <span className={styles.PhaseStepNow}>Now</span>
                        )}
                      </div>
                      <div className={styles.PhaseStepRange}>
                        <FontAwesomeIcon
                          icon={faCalendarDays}
                          className={styles.PhaseStepRangeIcon}
                        />
                        {range}
                      </div>
                      <div className={styles.PhaseStepDescription}>
                        {STEP_DESCRIPTION[step]}
                      </div>
                      {stepFooter && (
                        <div className={styles.PhaseStepDeadline}>
                          <FontAwesomeIcon
                            icon={faClock}
                            className={styles.PhaseStepDeadlineIcon}
                          />
                          {stepFooter}
                        </div>
                      )}
                    </div>
                    {idx < steps.length - 1 && (
                      <div
                        className={[
                          styles.PhaseConnector,
                          status === 'done'
                            ? styles.PhaseConnector_done
                            : styles.PhaseConnector_upcoming,
                        ].join(' ')}
                      />
                    )}
                  </React.Fragment>
                );
              })}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {userContext.user && (
        <Row
          className={`pb-4 ${
            actions.every((a) => a.hideOnMobile) ? 'd-none d-md-flex' : ''
          }`}
        >
          <Col>
            <div className={styles.ActionSectionHeader}>What you can do</div>
            <div className={styles.ActionGrid}>
              {actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className={[
                    styles.ActionCard,
                    action.hideOnMobile ? styles.ActionCard_hideOnMobile : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={action.onClick}
                >
                  <FontAwesomeIcon
                    icon={action.icon}
                    className={styles.ActionCardIcon}
                  />
                  <div>
                    <div className={styles.ActionCardLabel}>{action.label}</div>
                    <div className={styles.ActionCardDescription}>
                      {action.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Col>
        </Row>
      )}

      <Row className="pb-2">
        <Col>
          <h2>Frequently Asked Questions</h2>
        </Col>
      </Row>
      <Row>
        <Col>
          <Accordion
            className={styles.Accordion}
            activeKey={activeKey ?? undefined}
            onSelect={(eventKey) => {
              const next =
                Array.isArray(eventKey) ? eventKey[0] ?? null : eventKey ?? null;
              setActiveKey(next);
            }}
          >
            {order.map((key) => {
              const item = items[key];
              if (!item) return null;
              return (
                <Accordion.Item
                  key={key}
                  eventKey={key}
                  id={`faq-${key}`}
                >
                  <Accordion.Header>{item.title}</Accordion.Header>
                  <Accordion.Body>{item.body}</Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Col>
      </Row>

      <Row className="pt-4">
        <Col>
          <div className={styles.SupportBanner}>
            <div className={styles.SupportBannerText}>
              <div className={styles.SupportBannerTitle}>Still need help?</div>
              <div className={styles.SupportBannerDescription}>
                Our support team is here for you. Or call{' '}
                <a href="tel:+31777201200">+31 777 201 200</a>
                {!isExhibitor && (
                  <>
                    {' '}
                    ({formatShort(vts?.order_start)} –{' '}
                    {formatShort(vts?.order_end)}, daily 8:00 am – 5:30 pm)
                  </>
                )}
                .
              </div>
            </div>
            {userContext.user ? (
              <button
                type="button"
                className={styles.SupportBannerCta}
                onClick={() => navigate(MESSAGES_PATH)}
              >
                <FontAwesomeIcon icon={faComments} /> Chat with us
              </button>
            ) : (
              <div className={styles.SupportBannerHint}>
                Log in to chat with us
              </div>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
}
